import { useState } from "react";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  format, isSameMonth, isSameDay, addMonths, subMonths, parseISO,
} from "date-fns";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

const DOT = {
  pending: "bg-amber-500",
  approved: "bg-emerald-500",
  not_approved: "bg-red-500",
};

const PILL = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  not_approved: "bg-red-50 text-red-700 border-red-200",
};

export default function ParentCalendar({ visits }) {
  const [cursor, setCursor] = useState(new Date());
  const monthStart = startOfMonth(cursor);
  const days = eachDayOfInterval({
    start: startOfWeek(monthStart),
    end: endOfWeek(endOfMonth(cursor)),
  });

  const visitsOn = (day) =>
    visits.filter((v) => {
      try {
        return isSameDay(parseISO(v.visit_date), day);
      } catch {
        return false;
      }
    });

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="parent-calendar">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-display text-lg font-semibold text-slate-900">{format(cursor, "MMMM yyyy")}</h3>
        <div className="flex gap-1">
          <Button variant="outline" size="icon" data-testid="cal-prev" onClick={() => setCursor(subMonths(cursor, 1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" data-testid="cal-today" onClick={() => setCursor(new Date())}>
            Today
          </Button>
          <Button variant="outline" size="icon" data-testid="cal-next" onClick={() => setCursor(addMonths(cursor, 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold uppercase tracking-wide text-slate-400">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="pb-2">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {days.map((day) => {
          const inMonth = isSameMonth(day, cursor);
          const today = isSameDay(day, new Date());
          const dayVisits = visitsOn(day);
          return (
            <div
              key={day.toISOString()}
              className={`min-h-[92px] rounded-lg border p-1.5 text-left ${
                inMonth ? "border-slate-200 bg-white" : "border-transparent bg-slate-50/60"
              }`}
            >
              <div className={`mb-1 flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                today ? "bg-[#1e3a8a] text-white" : inMonth ? "text-slate-700" : "text-slate-300"
              }`}>
                {format(day, "d")}
              </div>
              <div className="space-y-1">
                {dayVisits.slice(0, 3).map((v) => (
                  <div
                    key={v.id}
                    title={`${v.start_time}–${v.end_time} · ${v.child_name}`}
                    className={`flex items-center gap-1 truncate rounded border px-1 py-0.5 text-[10px] font-medium ${
                      v.timeliness === "no_show" ? "border-red-200 bg-red-50 text-red-700" : PILL[v.status] || PILL.pending
                    }`}
                  >
                    <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${DOT[v.status] || DOT.pending}`} />
                    <span className="truncate">{v.start_time} {v.child_name}</span>
                  </div>
                ))}
                {dayVisits.length > 3 && (
                  <div className="px-1 text-[10px] text-slate-400">+{dayVisits.length - 3} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex flex-wrap gap-4 border-t border-slate-100 pt-3 text-xs text-slate-500">
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-500" /> Pending</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Approved</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-red-500" /> Not Approved</span>
      </div>
    </div>
  );
}
